import { useEffect, useRef, useState } from "react";
import { EventEmitter } from "../../utils/event-emitter";
import Events from "../../../const/events";
import ContactApi from "../../../api/contact-api";
import { QRCodeSVG } from "qrcode.react";
import Modal from "../dashboard-layout/components/modal/modalComponent";
import "./qr-viewer.scss";

const QRViewer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [qrData, setQrData] = useState("");
  const [contactId, setContactId] = useState("");
  const [contactName, setContactName] = useState("");
  const [contact, setContact] = useState(null);
  const [activeTab, setActiveTab] = useState("whatsapp");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const qrContainerRef = useRef(null);

  const _loadWhatsAppQRCode = async (contactId) => {
    try {
      const response = await ContactApi.private.getRoomLink(contactId);
      setQrData(response?.data?.data);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setError("Failed to load WhatsApp QR. Please try again.");
      setIsLoading(false);
    }
  };

  const _loadWeChatQRCode = async (contactId) => {
    try {
      const response = await ContactApi.private.getWeChatQrLink(contactId);
      setQrData(response?.data?.data?.qrImageUrl);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setError("Failed to load WeChat QR. Please try again.");
      setIsLoading(false);
    }
  };

  const _loadQRCode = async (contactId, platform = "whatsapp") => {
    setIsLoading(true);
    setError("");
    setQrData("");
    
    if (platform === "wechat") {
      await _loadWeChatQRCode(contactId);
    } else {
      await _loadWhatsAppQRCode(contactId);
    }
  };

  useEffect(() => {
    EventEmitter.subscribe(Events.SHOW_QR_CODE_POPUP, ({ id, title, contact, platforms = ["whatsapp"] }) => {
      // Prepare modal immediately for better perceived performance
      setContactId(id);
      setContactName(title);
      setContact(contact);
      setError("");
      setQrData("");
      setIsVisible(true);
      
      // Set default active tab based on available platforms
      const defaultTab = platforms.includes("whatsapp") ? "whatsapp" : "wechat";
      setActiveTab(defaultTab);
      
      _loadQRCode(id, defaultTab);
    });
    return () => {
      EventEmitter.cancelAll(Events.SHOW_QR_CODE_POPUP);
    };
  }, []);

  const handleTabSwitch = (platform) => {
    if (platform !== activeTab) {
      setActiveTab(platform);
      _loadQRCode(contactId, platform);
    }
  };

  const getAvailablePlatforms = () => {
    if (!contact?.platforms) return ["whatsapp"];
    
    const platforms = [];
    
    // Check for WhatsApp (requires valid phone)
    if (contact.platforms.some(p => p.on === "whatsapp") && contact.phone && contact.phone !== "N/A") {
      platforms.push("whatsapp");
      platforms.push("wechat"); // If they have WhatsApp, they can also use WeChat QR
    }
    
    return platforms.length > 0 ? platforms : ["whatsapp"]; // Fallback to whatsapp
  };

  if (!isVisible) {
    return null;
  }

  const closeModal = () => {
    setIsVisible(false);
    setIsLoading(false);
    setQrData("");
    setError("");
    setContact(null);
    setActiveTab("whatsapp");
  };


  return (
    <Modal
      isVisible={isVisible}
      onClose={closeModal}
      header={
        <div className="qrModalTitle">
          <i className="mdi mdi-qrcode-scan" /> Quick Connect
          <span className="title-sub">{contactName ? "to " + contactName : ""}</span>
        </div>
      }
      className={"qr-viewer"}
      cancelButtonLabel={"Close"}
      cancelButtonAction={closeModal}
    >
      {/* Tabs */}
      {getAvailablePlatforms().length > 1 && (
        <div className="mb-3">
          <div className="qr-platform-tabs">
            {getAvailablePlatforms().includes("whatsapp") && (
              <button
                className={`platform-tab ${activeTab === "whatsapp" ? "active" : ""}`}
                onClick={() => handleTabSwitch("whatsapp")}
                disabled={isLoading}
                type="button"
                role="tab"
                aria-selected={activeTab === "whatsapp"}
                aria-label="WhatsApp QR Code"
              >
                <i className="mdi mdi-whatsapp" />
                <span>WhatsApp</span>
              </button>
            )}
            {getAvailablePlatforms().includes("wechat") && (
              <button
                className={`platform-tab ${activeTab === "wechat" ? "active" : ""}`}
                onClick={() => handleTabSwitch("wechat")}
                disabled={isLoading}
                type="button"
                role="tab"
                aria-selected={activeTab === "wechat"}
                aria-label="WeChat QR Code"
              >
                <i className="mdi mdi-wechat" />
                <span>WeChat</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="qr-content-centered">
        <div className="qr-pane">
          <div className="qr-frame">
            {isLoading ? (
              <div className="qr-skeleton" aria-label="Loading QR" />
            ) : error ? (
              <div className="qr-error text-center">
                <i className="mdi mdi-alert-circle-outline" /> {error}
              </div>
            ) : (
              <div ref={qrContainerRef} className="qr-box p-3">
                {activeTab === "wechat" ? (
                  <img
                    src={qrData}
                    alt="WeChat QR Code"
                    style={{ width: "240px", height: "240px", objectFit: "contain" }}
                  />
                ) : (
                  <QRCodeSVG value={qrData} size={240} level={"H"} includeMargin={true} />
                )}
              </div>
            )}
          </div>
          {!isLoading && !error && qrData && (
            <div className="qr-caption">
              Scan to start a secure chat via {activeTab === "wechat" ? "WeChat" : "WhatsApp"}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default QRViewer;
